import { ArrowRight, BarChart3, Eye, ListTodo, Timer, Users, Zap } from "lucide-react";
import { Link } from "react-router";
import { AdSlot } from "@/components/ads/ad-slot";
import { Brand } from "@/components/logo";
import { SettingsButton } from "@/components/settings/settings-button";
import { Button } from "@/components/ui/button";
import { env } from "@/lib/env";

const features = [
  {
    icon: Users,
    title: "Real-time collaboration",
    description: "All players vote simultaneously and see results the moment cards are revealed.",
  },
  {
    icon: BarChart3,
    title: "Multiple voting systems",
    description: "Fibonacci, T-shirts, Powers of 2 and more — pick what fits your team.",
  },
  {
    icon: Eye,
    title: "Flexible reveal control",
    description: "Let anyone reveal or keep control with the room admin.",
  },
  {
    icon: Timer,
    title: "Auto-reveal & countdown",
    description: "Automatically reveal once everyone votes, with a fun countdown animation.",
  },
  {
    icon: Zap,
    title: "No login needed",
    description: "Just create a room, share the link, and start estimating in seconds.",
  },
  {
    icon: ListTodo,
    title: "Issue backlog",
    description: "Add your stories and move through them one by one during the session.",
  },
];

export function LandingPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center justify-between border-b border-border/40 px-6 py-4">
        <Link to="/" data-testid="brand">
          <Brand />
        </Link>
        <div className="flex items-center gap-2">
          <SettingsButton />
          <Button asChild size="sm">
            <Link to="/create-new">Create Room</Link>
          </Button>
        </div>
      </header>

      <main className="flex flex-1 flex-col">
        <section className="flex flex-1 flex-col items-center justify-center gap-8 px-6 py-24 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
            <Zap className="size-3.5" />
            Free · No login · Real-time
          </div>

          <div className="max-w-2xl space-y-4">
            <h1 className="text-5xl leading-tight font-extrabold tracking-tight sm:text-6xl">
              Estimate faster,
              <br />
              <span className="text-primary">together.</span>
            </h1>
            <p className="mx-auto max-w-xl text-lg text-muted-foreground">
              {env.appName} helps agile teams agree on story points quickly. Create a room, invite
              your team, and start voting in seconds — no account needed.
            </p>
          </div>

          <Button asChild size="lg" className="h-13 px-8 text-base font-semibold">
            <Link to="/create-new">
              Create a New Room
              <ArrowRight className="size-5" />
            </Link>
          </Button>

          <div className="mt-4 flex gap-3" aria-hidden="true">
            {["1", "2", "3", "5", "8", "13"].map((value) => (
              <div
                key={value}
                className="flex h-16 w-12 cursor-default items-center justify-center rounded-lg border border-border/60 bg-card text-lg font-bold shadow-md transition-transform duration-200 hover:-translate-y-2"
              >
                {value}
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-border/40 bg-card/30 px-6 py-20">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-12 text-center text-2xl font-bold">Everything your team needs</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {features.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="space-y-2 rounded-xl border border-border/50 bg-card p-5"
                >
                  <Icon className="size-6 text-primary" />
                  <h3 className="font-semibold">{title}</h3>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="flex justify-center px-6 pt-12">
          <AdSlot placement="landing" />
        </div>

        <section className="space-y-6 px-6 py-20 text-center">
          <h2 className="text-3xl font-bold">Ready to start estimating?</h2>
          <p className="mx-auto max-w-md text-muted-foreground">
            It takes under 10 seconds to create a room and invite your whole team.
          </p>
          <Button asChild size="lg" className="h-13 px-10 text-base font-semibold">
            <Link to="/create-new">
              Create New Room
              <ArrowRight className="size-5" />
            </Link>
          </Button>
        </section>
      </main>

      <footer className="border-t border-border/40 py-6 text-center text-xs text-muted-foreground">
        {env.appName} · Built for agile teams ·{" "}
        <Link to="/privacy" className="hover:text-foreground hover:underline">
          Privacy
        </Link>{" "}
        ·{" "}
        <Link to="/imprint" className="hover:text-foreground hover:underline">
          Imprint
        </Link>
      </footer>
    </div>
  );
}
